import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GraduationCap, RefreshCcw, Link2, Unlink, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fecha } from "@/lib/format";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const AVISOS = {
  conectado: ["success", "Cuenta de Google Classroom conectada"],
  error: ["error", "Google no autorizó la conexión: intente de nuevo"],
  sin_permiso: ["error", "Google no entregó el permiso de acceso: quite el acceso a la app en su cuenta y conéctela otra vez"],
};

export default function DocenteClassroom() {
  const [estado, setEstado] = useState(null);
  const [cursos, setCursos] = useState([]);
  const [sincronizando, setSincronizando] = useState(false);
  const [params, setParams] = useSearchParams();

  const cargar = async () => {
    const { data } = await api.get("/classroom/estado");
    setEstado(data);
    if (data.conectado && !data.requiere_reconexion) {
      api.get("/classroom/cursos").then((r) => setCursos(r.data))
        .catch((e) => toast.error(apiError(e)));
    } else {
      setCursos([]);
    }
  };

  useEffect(() => {
    const aviso = AVISOS[params.get("classroom")];
    if (aviso) {
      toast[aviso[0]](aviso[1]);
      params.delete("classroom");
      setParams(params, { replace: true });
    }
    cargar();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const conectar = async () => {
    try {
      const { data } = await api.get("/classroom/conectar");
      window.location.assign(data.url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const desconectar = async () => {
    await api.post("/classroom/desconectar");
    toast.success("Cuenta desconectada. Las tareas ya publicadas se conservan.");
    cargar();
  };

  const vincular = async (google_course_id, nombre, materia_id) => {
    setCursos(cursos.map((c) => (c.google_course_id === google_course_id
      ? { ...c, materia_id } : c)));
    try {
      await api.put("/classroom/vinculos", [{ google_course_id, nombre, materia_id: materia_id || null }]);
      toast.success(materia_id ? "Curso vinculado a la materia" : "Curso desvinculado");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const { data } = await api.post("/classroom/sincronizar");
      if (data.sin_vincular) {
        toast.warning("Primero empareje al menos un curso con una materia");
      } else {
        toast.success(`${data.tareas} tarea(s) sincronizada(s) de ${data.cursos} curso(s)` +
          (data.eliminadas ? ` · ${data.eliminadas} retirada(s)` : "") +
          (data.entregas ? ` · ${data.entregas} entrega(s)` : ""));
      }
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSincronizando(false);
    }
  };

  if (!estado) return <Loading />;

  return (
    <div className="space-y-8" data-testid="docente-classroom">
      <PageTitle title="Google Classroom"
                 subtitle="Conecte su cuenta y las tareas que publique en Classroom aparecerán automáticamente en el portal de los padres del grado.">
        {estado.conectado && (
          <div className="flex flex-wrap gap-2">
            <Button className="rounded-full" onClick={sincronizar} disabled={sincronizando}
                    data-testid="sincronizar-classroom">
              <RefreshCcw className={`mr-2 h-4 w-4 ${sincronizando ? "animate-spin" : ""}`} />
              {sincronizando ? "Sincronizando…" : "Sincronizar ahora"}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={desconectar}
                    data-testid="desconectar-classroom">
              <Unlink className="mr-2 h-4 w-4" /> Desconectar
            </Button>
          </div>
        )}
      </PageTitle>

      {!estado.configurado && (
        <section className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 sm:p-8"
                 data-testid="classroom-sin-configurar">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <h3 className="font-display text-base font-semibold">Falta configurar la conexión</h3>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Google exige credenciales propias del centro. En <span className="font-semibold">Google
            Cloud Console</span>: cree un proyecto, habilite la <span className="font-semibold">
            Google Classroom API</span>, configure la pantalla de consentimiento y cree un
            <span className="font-semibold"> ID de cliente de OAuth → Aplicación web</span>.
            Registre exactamente esta URL de retorno:
          </p>
          <code className="mt-4 block break-all rounded-md border border-border bg-card p-3 font-mono text-xs"
                data-testid="classroom-uri-retorno">
            {estado.uri_retorno}
          </code>
          <p className="mt-4 text-sm text-muted-foreground">
            Luego entregue el <span className="font-semibold">ID de cliente</span> y el
            <span className="font-semibold"> secreto</span> a la administración para activarla.
          </p>
        </section>
      )}

      {estado.configurado && !estado.conectado && (
        <section className="rounded-lg border border-border/60 bg-card p-6 sm:p-8"
                 data-testid="classroom-desconectado">
          <GraduationCap className="h-8 w-8 text-primary" />
          <h3 className="mt-4 font-display text-lg font-semibold">Conecte su cuenta de Classroom</h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Solo pedimos permisos de <span className="font-semibold">lectura</span>: sus cursos,
            las tareas que publique, sus temas y adjuntos, y el estado de entrega de sus
            estudiantes. Nunca publicamos ni modificamos nada en su Classroom.
          </p>
          <Button className="mt-6 rounded-full" onClick={conectar} data-testid="conectar-classroom">
            <Link2 className="mr-2 h-4 w-4" /> Conectar mi cuenta de Google
          </Button>
        </section>
      )}

      {estado.conectado && (
        <>
          <section className="rounded-lg border border-border/60 bg-card p-6"
                   data-testid="classroom-conectado">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="flex items-center gap-2 font-display text-base font-semibold">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {estado.correo || "Cuenta conectada"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {estado.ultima_sync
                    ? `Última sincronización: ${fecha(estado.ultima_sync)}`
                    : "Aún no ha sincronizado sus tareas"}
                  {" · "}Se sincroniza también cada mañana de forma automática
                </p>
              </div>
              {estado.requiere_reconexion && (
                <Badge variant="destructive" data-testid="classroom-reconectar">
                  Permiso revocado: vuelva a conectar
                </Badge>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-border/60 bg-card p-6 sm:p-8">
            <h3 className="font-display text-base font-semibold">Empareje sus cursos con las materias</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              La materia define el grado que verá las tareas. Los cursos sin materia no se sincronizan.
            </p>
            <div className="mt-6 space-y-4" data-testid="cursos-classroom">
              {cursos.map((c) => (
                <div key={c.google_course_id}
                     className="flex flex-col gap-3 rounded-md border border-border/60 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{c.nombre}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.seccion || "Sin sección"}
                      {c.enlace && (
                        <a href={c.enlace} target="_blank" rel="noreferrer"
                           className="ml-2 inline-flex items-center gap-1 text-primary hover:underline">
                          <ExternalLink className="h-3 w-3" /> ver en Classroom
                        </a>
                      )}
                    </p>
                  </div>
                  <select value={c.materia_id || ""} data-testid={`vincular-${c.google_course_id}`}
                          onChange={(e) => vincular(c.google_course_id, c.nombre, e.target.value)}
                          className="rounded-md border border-border bg-background px-3 py-2 text-sm">
                    <option value="">Sin vincular</option>
                    {estado.materias.map((m) => (
                      <option key={m.id} value={m.id}>{`${m.nombre} · ${m.grado}`}</option>
                    ))}
                  </select>
                </div>
              ))}
              {cursos.length === 0 && (
                <Empty testId="classroom-sin-cursos">
                  No encontramos cursos activos donde usted sea docente en Classroom.
                </Empty>
              )}
            </div>
          </section>

          {estado.vinculos.some((v) => v.materia_id) && (
            <section className="rounded-lg border border-border/60 bg-card p-6"
                     data-testid="resumen-vinculos">
              <h3 className="font-display text-base font-semibold">Tareas publicadas por curso</h3>
              <div className="mt-4 space-y-2 text-sm">
                {estado.vinculos.filter((v) => v.materia_id).map((v) => (
                  <div key={v.google_course_id} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">
                      {v.nombre} → <span className="font-semibold text-foreground">{v.materia_nombre}</span>
                      {v.grado ? ` · ${v.grado}` : ""}
                    </span>
                    <Badge variant="secondary" data-testid={`tareas-curso-${v.google_course_id}`}>
                      {v.tareas} tarea(s)
                    </Badge>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
